import ProUnlockPersistence from './pro-unlock.persistence';
import { useProUnlockStore } from './pro-unlock.store';
import type { ProUnlockResult, ProUnlockSource } from './types/pro-unlock-source.type';
import { ErrorService } from '@/core/shared/errors/error.service';
import MeService from '@/core/shared/me/me.service';
import { SearchService } from '../search/search.service';
import SelectedCandidateService from '../selected-candidate/selected-candidate.service';

export default class ProUnlockService {
  private persistence = new ProUnlockPersistence();
  private searchService = new SearchService();
  private meService = new MeService();
  private selectedCandidateService = new SelectedCandidateService();
  private store = useProUnlockStore();

  /**
   * TODO: Remove this method when the old search page is removed
   * (https://linear.app/factoryfix/issue/ONE-3172/remove-legacy-code-when-we-remove-the-old-sourcing-search)
   */
  async unlockProUserProfilesLegacy(
    userProfileIds: number[],
    source: ProUnlockSource,
  ): Promise<ProUnlockResult | undefined> {
    if (!this.meService.employer?.id || !userProfileIds.length || !source) return;

    // Route the legacy unlock through the store-aware path while retaining legacy refreshes.
    const results = await this.unlockProUserProfiles(userProfileIds, source);
    // this.$mixpanelAnalytics.proUnlock({
    //   source,
    //   numberOfPros: userProfileIds.length,
    // });
    this.searchService.searchCandidates();

    const selectedCandidate = this.selectedCandidateService.candidate;
    const selectedOutcome = selectedCandidate?.id
      ? results?.enrichmentResults?.[selectedCandidate.id]
      : undefined;
    // The modern path already refreshes an immediately-updated selected candidate.
    if (selectedCandidate?.id && selectedOutcome?.outcome !== 'updated') {
      this.selectedCandidateService.fetchCandidate(selectedCandidate.id);
    }

    return results;
  }

  /**
   * Unlocks the pro user profiles
   * @param userProfileIds - The user profile ids to unlock
   * @param source - The source of the unlock
   * @returns The results of the unlock
   */
  async unlockProUserProfiles(
    userProfileIds: number[],
    source: ProUnlockSource,
    searchSessionId?: string,
  ): Promise<ProUnlockResult | undefined> {
    const employerId = this.meService.employer?.id;
    if (!employerId || !userProfileIds.length || !source) return;

    this.store.markLoading(userProfileIds);
    try {
      const result = await this.persistence.unlockProUserProfiles({
        employerId,
        userProfileIds,
        source,
        ...(searchSessionId ? { searchSessionId } : {}),
      });

      this.store.setOutcomes(result?.unlockedProfileIds ?? [], result?.enrichmentResults ?? {});
      const checkingIds = (result?.unlockedProfileIds ?? []).filter(
        (id) => result?.enrichmentResults?.[id]?.outcome === 'checking',
      );
      if (checkingIds.length) {
        this.store.startEnrichmentStatusPolling(checkingIds);
      }
      // Clear loading for ids not in `unlockedProfileIds` (rate-limit, deduped).
      this.store.clearLoading(userProfileIds);

      this.refetchSelectedCandidateIfPdlUpdated(result);

      return result;
    } catch (error) {
      this.store.clearLoading(userProfileIds);
      throw error;
    }
  }

  // Re-read the selected candidate directly from ff-rest when PDL wrote new
  // contact info; the Talent Search index lags seconds to minutes behind.
  private refetchSelectedCandidateIfPdlUpdated(result: ProUnlockResult | undefined): void {
    const selectedCandidateId = this.selectedCandidateService.candidate?.id;
    if (!selectedCandidateId || !result) return;

    const outcome = result.enrichmentResults?.[selectedCandidateId];
    if (outcome?.outcome === 'updated') {
      this.selectedCandidateService.fetchCandidate(selectedCandidateId).catch((error) => {
        ErrorService.captureException(error);
      });
    }
  }
}
